import {ActivatedRoute, Router} from '@angular/router';

import {Observable, Subscription} from 'rxjs';

import {
    ApiError,
    ApiResponse,
    AuthApiService,
    ChargeGroupModel,
    ChargeScheduleInterface,
    ChargeScheduleModel,
    ChargeScheduleSearchModel,
    ChargeSchedulesService, FeeValidityEnum,
    HttpStatusCodesEnum,
    ReportsService,
    TradingAccountFilterModel,
    TradingAccountReportInterface,
    TradingAccountReportModel,
    TradingAccountStatusesEnum,
    TradingPlatformInfoInterface
} from '@spa/api';

import {Helper, SelectControl} from '../../../shared/classes';
import {
    Pagination,
    ReportTableCheckboxClickEventInterface,
    ReportTableConfigInterface,
    ReportTableData,
    ReportTableDataRow
} from '../../../shared/components';
import {AlertsService, CacheCollectionService} from '../../../shared/services';
import {ItemsPerPageSelector} from '../../../shared/widgets/filter';
import {InitialComponentDataValidationException} from '../../../shared/exceptions';

import {PanelPaths} from '../../../shared/paths';

import {EventsService} from '../services';
import {TradingAccountTypesEnum} from '../enums';
import {AccountsReportResolverDataInterface, SortColumnInterface} from '../interfaces';

import {ReportComponent} from './report-component';
import {AlertTypesEnum, UnspecifiedKeysEnum} from 'appShared/enums';
import {FilterModel} from '@spa/api/lib/common';
import {
    AccountChargeScheduleFilterSelector,
    AccountFeeCategorySelector,
    AccountFeeGroupSelector,
    AddFeeDialogConfig
} from 'appAssets/descriptors';
import {FormGroup} from '@angular/forms';
import {BulkAddFeeComponent} from 'appEntries/panel/dialogs/bulk-add-fee';
import {MatDialog} from '@angular/material/dialog';

export abstract class AccountsReportComponent
    extends ReportComponent<TradingAccountReportInterface, TradingAccountReportModel> {

    private activatedRouteSubscription: Subscription;
    private readonly feeFilterSubscriptions: Subscription[];


    protected authApiService: AuthApiService;
    protected alertsService: AlertsService;
    protected activatedRouter: ActivatedRoute;
    protected reportsService: ReportsService;
    protected accountsInitialFilter: TradingAccountFilterModel;
    protected tradingPlatforms: TradingPlatformInfoInterface[];
    protected allFeeGroups: ChargeGroupModel[];
    protected currentChargeScheduleModels: ChargeScheduleModel[];
    public dataTableWithFeesConfig: ReportTableConfigInterface;
    public useDataTableWithFees = false;
    public allAccountsSelected = false;
    public addFeeButtonVisible = false;
    public updateFeeButtonVisible = false;

    public panelPaths: typeof PanelPaths;
    public form: FormGroup;

    protected constructor(
        protected dialog: MatDialog,
        protected chargeSchedulesService: ChargeSchedulesService,
        alertsService: AlertsService,
        authApiService: AuthApiService,
        activatedRouter: ActivatedRoute,
        reportsService: ReportsService,
        router: Router,
        eventsService: EventsService,
        cacheCollectionService: CacheCollectionService
    ) {
        super(router, eventsService, cacheCollectionService);

        this.feeFilterSubscriptions = [];
        this.alertsService = alertsService;
        this.activatedRouter = activatedRouter;
        this.reportsService = reportsService;
        this.authApiService = authApiService;

        this.panelPaths = PanelPaths;
        this.filterModel = this.getInitialFilterModel();
    }

    private fixColumnSelector(columnSelector: string): string {
        switch (columnSelector) {
            case TradingAccountReportModel.getAccountStatusNameSelector():
                return TradingAccountReportModel.getAccountStatusSelector();
            case TradingAccountReportModel.getAccountTypeNameSelector():
                return TradingAccountReportModel.getIsCashAccountSelector();
            case TradingAccountReportModel.getTradingPlanTypeNameSelector():
                return TradingAccountReportModel.getTradingPlanTypeSelector();
            case TradingAccountReportModel.getAccountTradingPlanStatusNameSelector():
                return TradingAccountReportModel.getAccountPlanStatusSelector();
            default:
                return columnSelector;
        }
    }
    private fixLoadColumnSelector(columnSelector: string): string {
        switch (columnSelector) {
            case TradingAccountReportModel.getAccountStatusSelector():
                return TradingAccountReportModel.getAccountStatusNameSelector();
            case TradingAccountReportModel.getIsCashAccountSelector():
                return TradingAccountReportModel.getAccountTypeNameSelector();
            case TradingAccountReportModel.getTradingPlanTypeSelector():
                return TradingAccountReportModel.getTradingPlanTypeNameSelector();
            case TradingAccountReportModel.getAccountPlanStatusSelector():
                return TradingAccountReportModel.getAccountTradingPlanStatusNameSelector();
            default:
                return columnSelector;
        }
    }

    private initFeeValidity(feeValidity: number): void {
        this.updateFeeButtonVisible = false;
        this.addFeeButtonVisible = false;

        switch (feeValidity) {
            case FeeValidityEnum.Missing:
                this.addFeeButtonVisible = true;
                break;
            case FeeValidityEnum.Assigned:
            case FeeValidityEnum.AssignedAndValid:
            case FeeValidityEnum.AssignedAndExpired:
                this.updateFeeButtonVisible = true;
                break;
        }
    }

    private initFeeCategory(feeCategoryCode: string): void{
        const feeControls = this.filterControls.find(f => f.name === AccountChargeScheduleFilterSelector);
        const feeGroupControl = feeControls.controls.find(f => f.name === AccountFeeGroupSelector) as SelectControl;
        const feeScheduleControl =
            feeControls.controls.find(f => f.name === TradingAccountFilterModel.getChargeScheduleReferenceIdSelector()) as SelectControl;
        const feeValidityControl =
            feeControls.controls.find(f => f.name === TradingAccountFilterModel.getFeeValiditySelector()) as SelectControl;

        feeValidityControl.resetValue();
        feeGroupControl.options.splice(0);
        feeGroupControl.resetValue(true);
        feeScheduleControl.options.splice(0);

        const selectedFeeGroup = this.allFeeGroups.find(feeGroup => feeGroup.name === feeCategoryCode);

        if (selectedFeeGroup) {
                selectedFeeGroup.chargeTypes.forEach(chargeType => {
                    feeGroupControl.options.push(
                        {
                            label: chargeType.name,
                            value: chargeType.code
                        }
                    );
                });
            }
    }

    private initFeeGroup(feeGroupCode: string): void{
        const feeControls = this.filterControls.find(f => f.name === AccountChargeScheduleFilterSelector);
        const feeScheduleControl =
            feeControls.controls.find(f => f.name === TradingAccountFilterModel.getChargeScheduleReferenceIdSelector()) as SelectControl;

        const selectedValue = feeScheduleControl.value;
        feeScheduleControl.options.splice(0);

        const searchModel = new ChargeScheduleSearchModel(feeGroupCode);
        const searchSchedulesSubscription = this.chargeSchedulesService.searchSchedules(searchModel)
            .subscribe(feesResponse => {
                const responseData = feesResponse.data as ChargeScheduleInterface[];
                this.currentChargeScheduleModels = [];

                if (feesResponse.status !== HttpStatusCodesEnum.Ok || !responseData) {
                    this.alertsService.addAlert('Cannot find any charge schedule', AlertTypesEnum.Warning);
                }
                else {
                    const allFees = responseData.map(item => new ChargeScheduleModel(item));
                    const fees = allFees.filter(fee => fee.getValidityPerAccount());

                    this.currentChargeScheduleModels = fees;

                    if (this.currentChargeScheduleModels.length === 0) {
                        this.alertsService.addAlert('Cannot find any charge schedule', AlertTypesEnum.Warning);
                    }
                    else {
                        this.currentChargeScheduleModels.forEach(feeSchedule => {
                            feeScheduleControl.options.push(
                                {
                                    label: feeSchedule.chargeScheduleName,
                                    value: feeSchedule.chargeScheduleRefId
                                }
                            );
                        });

                        if (selectedValue) {
                            const feeFormControl = this.form.controls[TradingAccountFilterModel.getChargeScheduleReferenceIdSelector()];
                            feeFormControl.setValue(selectedValue);
                        }
                    }
                }
            });

        this.feeFilterSubscriptions.push(searchSchedulesSubscription);
    }

    initFeeFilters(): void {
        const feeCategoryFormControl = this.form.controls[AccountFeeCategorySelector];
        const feeGroupFormControl = this.form.controls[AccountFeeGroupSelector];
        const feeValidityFormControl = this.form.controls[TradingAccountFilterModel.getFeeValiditySelector()];
        const feeScheduleFormControl = this.form.controls[TradingAccountFilterModel.getChargeScheduleReferenceIdSelector()];
        const feeControls = this.filterControls.find(f => f.name === AccountChargeScheduleFilterSelector);
        const feeScheduleControl =
            feeControls.controls.find(f => f.name === TradingAccountFilterModel.getChargeScheduleReferenceIdSelector()) as SelectControl;

        const feeValidityControlSubscription = feeValidityFormControl.valueChanges.subscribe(feeValidity => {
            this.initFeeValidity(feeValidity);
        });

        const feeCategoryControlSubscription = feeCategoryFormControl.valueChanges.subscribe(feeCategoryCode => {
            this.initFeeCategory(feeCategoryCode);
            feeScheduleControl.resetValue(true);
            feeScheduleFormControl.setValue(null);
        });

        const feeGroupControlSubscription = feeGroupFormControl.valueChanges.subscribe(feeGroupCode => {
            this.initFeeGroup(feeGroupCode);
        });

        if (feeCategoryFormControl.value) {
            this.initFeeCategory(feeCategoryFormControl.value);
        }

        if (feeGroupFormControl.value) {
            this.initFeeGroup(feeGroupFormControl.value);
        }

        if ((typeof feeValidityFormControl.value !== 'undefined') ) {
            this.initFeeValidity(feeValidityFormControl.value);
        }

        this.feeFilterSubscriptions.push(feeCategoryControlSubscription);
        this.feeFilterSubscriptions.push(feeGroupControlSubscription);
        this.feeFilterSubscriptions.push(feeValidityControlSubscription);
    }

    onFilterChanged(data: { [key: string]: string | number | boolean }): void {
        const feeCategory = data[AccountFeeCategorySelector];
        const fee = data[TradingAccountFilterModel.getChargeScheduleReferenceIdSelector()];

        if (feeCategory) {
            const statusControl = this.filterControls.find(f => f.name === TradingAccountFilterModel.getAccountStatusSelector());
            statusControl.shown = true;

            if (this.form.controls[statusControl.name].value !== TradingAccountStatusesEnum.Active) {
                const statusFormControl  = this.form.controls[statusControl.name];
                statusFormControl.setValue(TradingAccountStatusesEnum.Active);
            }
        }

        if (feeCategory && !fee) {
            return;
        }

        const filterModel = this.getFilterModel(data);
        this.reloadData(filterModel);
    }

    onInit(): void {
        this.form = new FormGroup({});

        super.onInit();
        // tslint:disable-next-line:max-line-length
        const sortedColumnCache = this.cacheCollectionService.getItem(this.componentStorageKeyProvider.getFilterModelKey())?.data as FilterModel;
        // tslint:disable-next-line:max-line-length
        this.sortedColumn = sortedColumnCache ? {selector: this.fixLoadColumnSelector(sortedColumnCache.order_column), ascending: sortedColumnCache.order_ascending} :  { selector: null, ascending: false };
        this.activatedRouteSubscription = this.activatedRouter.data
            .subscribe((result: { data: ApiResponse<ApiError[] | AccountsReportResolverDataInterface> }) => {
                this.doInitialDataValidationCheck(result.data, () => {
                    const resolverData = result.data.data as AccountsReportResolverDataInterface;

                    this.filterControls = resolverData.filterControls;
                    this.dataTableConfig = resolverData.dataTableConfig;
                    this.dataTableWithFeesConfig = resolverData.dataTableWithFeesConfig;
                    this.modelsCollection = resolverData.modelsCollection;
                    this.accountsInitialFilter = resolverData.filterModel;
                    this.tradingPlatforms = resolverData.tradingPlatforms;
                    this.allFeeGroups = resolverData.allFeeGroups;

                    const start = Pagination.calculateStartPagesChunkIndex(this.paginationConfig.currentPage,
                        this.paginationConfig.itemsPerPage, this.paginationConfig.pagesChunk);
                    const end = Pagination.calculateEndPagesChunkIndex(this.paginationConfig.currentPage,
                        this.paginationConfig.itemsPerPage, this.paginationConfig.pagesChunk);
                    const paginatedCollection = this.paginateCollection<TradingAccountReportModel>(this.modelsCollection, start, end);

                    this.dataTableWithFeesConfig.rows =
                        this.dataTableConfig.rows = this.createDataSource(paginatedCollection).map((row: ReportTableDataRow) => {
                        row.dataItems.map((item: ReportTableData) => {
                            if (this.dataTableConfig.hiddenColumnIds.indexOf(item.colId) >= 0) {
                                item.hidden = true;
                            }
                        });

                        return row;
                    });

                    this.paginationConfig.totalItems = this.modelsCollection.length;
                });
            });
    }

    onDestroy(): void {
        if (this.activatedRouteSubscription) {
            this.activatedRouteSubscription.unsubscribe();
        }

        if (this.searchSubscription) {
            this.searchSubscription.unsubscribe();
        }

        if (this.feeFilterSubscriptions) {
            this.feeFilterSubscriptions.forEach(s => s.unsubscribe());
        }
    }

    getReportHandler(
        filter: TradingAccountFilterModel
    ): Observable<ApiResponse<ApiError[] | TradingAccountReportInterface[]>> {
        this.showLoader = true;

        return this.reportsService.getTradingAccountsReport(filter);
    }

    getFilterModel(data: { [p: string]: number | string | boolean }): TradingAccountFilterModel {
        const normalizeOptionalString = (value: string): string | null => value ? value : null;
        const itemsPerPage = data[ItemsPerPageSelector] as number;
        const itemsPerPagesChunk = Pagination.calculateItemsPerPagesChunk(itemsPerPage);
        const status = data[TradingAccountFilterModel.getAccountStatusSelector()] as number;
        const type = data[TradingAccountFilterModel.getIsCashAccountSelector()];
        const platform = data[TradingAccountFilterModel.getAccountTradingPlatformIdSelector()] as number;
        const subType = data[TradingAccountFilterModel.getAccountSubTypeSelector()] as number;
        const currency = normalizeOptionalString(data[TradingAccountFilterModel.getCurrencySelector()] as string);
        const createDateFrom = normalizeOptionalString(
            data[TradingAccountFilterModel.getAccountCreatedDateFromSelector()] as string
        );
        const createDateTo = normalizeOptionalString(
            data[TradingAccountFilterModel.getAccountCreatedDateToSelector()] as string
        );
        const approvedDateFrom = normalizeOptionalString(
            data[TradingAccountFilterModel.getAccountApprovedDateFromSelector()] as string
        );
        const approvedDateTo = normalizeOptionalString(
            data[TradingAccountFilterModel.getAccountApprovedDateToSelector()] as string
        );
        const chargeScheduleRefId = normalizeOptionalString(
            data[TradingAccountFilterModel.getChargeScheduleReferenceIdSelector()] as string
        );
        const feeValidity = data[TradingAccountFilterModel.getFeeValiditySelector()] as number;
        const orderAscending = this.sortedColumn ? this.sortedColumn.ascending : false;
        const feeValidFrom = normalizeOptionalString(
            data[TradingAccountFilterModel.getChargeScheduleStartDateSelector()] as string
        );
        const feeValidTo = normalizeOptionalString(
            data[TradingAccountFilterModel.getChargeScheduleEndDateSelector()] as string
        );
        const includeAccessedUsers = data[TradingAccountFilterModel.getIncludeAccessedUsersSelector()] as boolean;
        const closeoutPlanId = data[TradingAccountFilterModel.getCloseoutPlanIdSelector()];

        this.useDataTableWithFees = !!chargeScheduleRefId;

        const orderColumn = this.sortedColumn?.selector
            ? this.fixColumnSelector(this.sortedColumn.selector) : TradingAccountReportModel.getAccountCreatedDateSelector();

        let isCashAccount = null;

        switch (type) {
            case TradingAccountTypesEnum.Trading:
                isCashAccount = false;
                break;
            case TradingAccountTypesEnum.Cash:
                isCashAccount = true;
                break;
        }

        return new TradingAccountFilterModel(
            null,
            null,
            null,
            null,
            null,
            normalizeOptionalString(data[TradingAccountFilterModel.getOwnerUserNameSelector()] as string),
            normalizeOptionalString(data[TradingAccountFilterModel.getGrouReferenceIdSelector()] as string),
            null,
            null,
            null,
            normalizeOptionalString(data[TradingAccountFilterModel.getAccountReferenceIdSelector()] as string),
            (status !== TradingAccountStatusesEnum.Unspecified) ? [status] : null,
            createDateFrom,
            createDateTo,
            isCashAccount,
            parseFloat(data[TradingAccountFilterModel.getBalanceFromSelector()] as string),
            parseFloat(data[TradingAccountFilterModel.getBalanceToSelector()] as string),
            platform,
            Pagination.defaultPagesChunk,
            itemsPerPagesChunk,
            orderColumn,
            orderAscending,
            currency,
            approvedDateFrom,
            approvedDateTo,
            chargeScheduleRefId,
            feeValidity,
            subType !== 0 ? subType : null,
            feeValidFrom,
            feeValidTo,
            normalizeOptionalString(data[TradingAccountFilterModel.getTradingPlanNameSelector()] as string),
            data[TradingAccountFilterModel.getTradingPlanTypeEnumSelector()] !== UnspecifiedKeysEnum.UnspecifiedKey ?
                data[TradingAccountFilterModel.getTradingPlanTypeEnumSelector()] as number : null,
            normalizeOptionalString(data[TradingAccountFilterModel.getTradingPlanPhaseSelector()] as string),
            data[TradingAccountFilterModel.getAccountTradingPlanStatusEnumSelector()] !== UnspecifiedKeysEnum.UnspecifiedKey ?
                data[TradingAccountFilterModel.getAccountTradingPlanStatusEnumSelector()] as number : null,
            data[TradingAccountFilterModel.getTradingPlanBpEqAmountFromSelector()] as number,
            data[TradingAccountFilterModel.getTradingPlanBpEqAmountToSelector()] as number,
            normalizeOptionalString(data[TradingAccountFilterModel.getTradingPlanCreatedOnFromSelector()] as string),
            normalizeOptionalString(data[TradingAccountFilterModel.getTradingPlanCreatedOnToSelector()] as string),
            includeAccessedUsers,
            (closeoutPlanId === undefined || closeoutPlanId === null || closeoutPlanId === '') ? null : Number(closeoutPlanId)
        );
    }

    mapFilterResults(results: TradingAccountReportInterface[]): TradingAccountReportModel[] {
        return results.map((account: TradingAccountReportInterface) => {
            const model = new TradingAccountReportModel(account);

            model.accountTypeName = Helper.textsHelper().getTradingAccountTypeName(model.isCashAccount);

            if (!model.isCashAccount) {
                model.accountStatusName = Helper.textsHelper().getTradingAccountStatusName(model.accountStatus);
            } else {
                model.accountStatusName = Helper.textsHelper().getCashAccountStatusName(model.accountStatus);
            }

            model.tradingPlanTypeName = Helper.textsHelper().getTradingPlanTypeName(model.tradingPlanType);
            model.accountTradingPlanStatusName = Helper.textsHelper().getAccountPlanStatusName(model.accountPlanStatus);


            return model;
        });
    }

    validateInitialData(response: ApiResponse<ApiError[] | AccountsReportResolverDataInterface>): void {
        if (response.status !== HttpStatusCodesEnum.Ok) {
            throw new InitialComponentDataValidationException(response.messages);
        }
    }

    onRowClicked(accountReferenceId: string | number): void {
        const filteredAccounts = this.modelsCollection.filter((item: TradingAccountReportModel) => {
            return item.accountReferenceId === accountReferenceId;
        });

        if (filteredAccounts.length !== 0) {
            const accountType =
                filteredAccounts[0].isCashAccount ? TradingAccountTypesEnum.Cash : TradingAccountTypesEnum.Trading;

            Helper.navigateToAccountsView(accountType, accountReferenceId as string, this.authApiService, this.router);
        }
    }

    sortData(column: SortColumnInterface): void {
        const sortColumnSelector = this.fixColumnSelector(column.selector);

        this.sortedColumn = column;
        this.filterModel.setOrderColumn(sortColumnSelector);
        this.filterModel.setOrderAscending(this.sortedColumn.ascending);

        super.forceFilterData(this.filterModel);
    }

    onPageChanged(data: { start: number; end: number, currentPage: number }): void {
        super.onPageChanged(data);
        this.dataTableWithFeesConfig.rows = this.dataTableConfig.rows;
    }

    filterResultSuccessHandler(
        initialPaginationPage: number,
        paginateFrom: number,
        paginateTo: number,
        response: ApiResponse<ApiError[] | TradingAccountReportInterface[]>
    ): void {
        this.showLoader = false;
        super.filterResultSuccessHandler(initialPaginationPage, paginateFrom, paginateTo, response);
        this.dataTableWithFeesConfig.rows = this.dataTableConfig.rows;
    }

    onHeaderSelectionChanged(event: ReportTableCheckboxClickEventInterface[]): void {
        const checkedCount = event.filter(e => e.value).length;
        const unCheckedCount = event.length - checkedCount;
        this.allAccountsSelected = unCheckedCount === 0;
    }

    onAddFeePlanClicked(): void {
        const accountReferenceIds = this.dataTableWithFeesConfig.rows.filter(r => r.checked).map(a => a.rowId);

        if (accountReferenceIds.length === 0) {
            this.alertsService.addAlert('Please select accounts', AlertTypesEnum.Warning);

            return;
        }

        const accounts = this.modelsCollection.filter(a => accountReferenceIds.indexOf(a.accountReferenceId) >= 0);

        this.dialog.open(BulkAddFeeComponent, {
            width: '500px',
            data: {
                config: AddFeeDialogConfig(),
                filterControls: this.filterControls,
                allFeeGroups: this.allFeeGroups,
                filterModelFunction: this.getFilterModel,
                isValidityUpdate: false,
                ignoreSelected: this.allAccountsSelected,
                accounts
            }
        });
    }

    onEditFeePlanValidityClicked(): void {
        const accountReferenceIds = this.dataTableWithFeesConfig.rows.filter(r => r.checked).map(a => a.rowId);

        if (accountReferenceIds.length === 0) {
            this.alertsService.addAlert('Please select accounts', AlertTypesEnum.Warning);

            return;
        }

        const accounts = this.modelsCollection.filter(a => accountReferenceIds.indexOf(a.accountReferenceId) >= 0);

        const feeScheduleFormControl = this.form.controls[TradingAccountFilterModel.getChargeScheduleReferenceIdSelector()];

        const modelToUpdate = this.currentChargeScheduleModels?.find(f => f.chargeScheduleRefId === feeScheduleFormControl.value);

        if (!modelToUpdate) {
            this.alertsService.addAlert('Please select fee schedule to update validity');

            return;
        }

        this.dialog.open(BulkAddFeeComponent, {
            width: '500px',
            data: {
                config: AddFeeDialogConfig(),
                filterControls: this.filterControls,
                allFeeGroups: this.allFeeGroups,
                filterModelFunction: this.getFilterModel,
                isValidityUpdate: true,
                ignoreSelected: this.allAccountsSelected,
                modelToUpdate,
                accounts,
            }
        });
    }
}
